/* eslint-disable no-nested-ternary */
import getFilterActiveStatus from "@Pages/Search/utils/getFilterActiveStatus";
import getFilterLabel from "@Pages/Search/utils/getFilterLabel";
import { useBudgetPriceCeiling } from "../Filters/Budget/usePriceHistogram";
import { AppState } from "@Store/index";
import { shallowEqual, useSelector } from "react-redux";
import {
  MAIN_FILTERS_WITH_SORT,
  SORT_FILTER,
  HOUSING_FEST_FILTER,
  LOCALITY_FILTER,
} from "../../data/filtersList";
import Badge, { FilterNameType } from "../Badge/Badge";
import FiltersBadgeLazyLoader from "./components/FiltersBadgeLazyLoader";
import reorderFilter, {
  getBadgeValue,
  getLocalityOrUniversityTitle,
  hasAnyAmenitiesOptions,
  hasAnyBillsIncludedOptions,
  shouldHideAmenitiesOrBillsPill,
} from "./filtersBadgeHelper";
import wrapperHOC from "@Utils/wrapperHOC";
import useWhitelabel from "hooks/useWhitelabel";
import styles from "./FiltersBadgeGroupDesktop.module.scss";
import { useRef } from "react";
import { useFeatureIsOn, useGrowthBook } from "@growthbook/growthbook-react";
import { GROWTH_BOOK_FEATURE_FLAG_NAMES } from "constants/index";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import useAnimationWithCookie from "hooks/useAnimationWithCookie";
import Chevron from "@Icons/Chevron";
import { useAmberSales } from "hooks/useAmberSales";
import { SmartFilterPill } from "../SmartFilters";
import config from "@Config/index";
import useTranslationHook from "hooks/useTranslationHook";
import { useIsNewNativeApp } from "hooks/useIsApp";
import badgeStyles from "../Badge/badge.module.scss";

type ActiveFilterType = (typeof MAIN_FILTERS_WITH_SORT)[number]["name"] | "";

type FilterBadgeGroup = {
  onFilterClick: (name?: ActiveFilterType) => void;
  filtersState: any;
  dynamiceFilterCount?: any;
  isFilterCountLoading?: boolean;
};
type BadgePropsType = {
  name: any;
  title: string;
  whitelabelTitle: string;
  activeLabel: any;
  isActive: boolean;
  icon: any;
  index: number;
};

const HousingTypeChevron = () => (
  <span className={styles.housingTypeChevron}>
    <Chevron />
  </span>
);

const FiltersBadgeGroup = ({
  onFilterClick,
  filtersState,
  dynamiceFilterCount,
  isFilterCountLoading,
}: FilterBadgeGroup) => {
  const {
    filters,
    region,
    inventoryCount,
    sourceCountry,
    countryShortName,
    currencyExchangeRates,
    filterUniversityList,
    filterLocalityList,
  } = useSelector(
    ({ searchPageData, appData }: AppState) => ({
      filters: searchPageData.filters,
      region: searchPageData.region,
      inventoryCount: searchPageData.searchList?.meta?.count,
      sourceCountry: appData.country,
      countryShortName: searchPageData.region?.location?.country?.short_name,
      currencyExchangeRates: appData.currencyExchangeRates,
      filterUniversityList: searchPageData.filterUniversityList?.data || [],
      filterLocalityList: searchPageData.filterLocalityList?.data || [],
    }),
    shallowEqual,
  );
  const budgetPriceCeiling = useBudgetPriceCeiling();
  const { isWhitelabel, isUniversityPartner, whitelabelConfig } = useWhitelabel();
  const { isSaleOfferAvailable } = useAmberSales({ page: "SEARCH" });
  const { t } = useTranslationHook();

  const shouldShowHousingTypeFilter =
    isWhitelabel &&
    isUniversityPartner &&
    whitelabelConfig?.is_university_properties_section_enabled;

  let ORDERED_FILTERS = reorderFilter(region, true);

  const housingTypeFilter = ORDERED_FILTERS?.find((filter: any) => filter?.name === "Housing Type");
  ORDERED_FILTERS = ORDERED_FILTERS?.filter(
    (filter: any) =>
      filter.name !== "Guarantor" &&
      filter.name !== "Housing Type" &&
      filter.name !== "Housing Fest",
  );

  const isBillsIncludedActive = !!getFilterActiveStatus("Bills Included", filters);
  if (
    shouldHideAmenitiesOrBillsPill({
      isActive: isBillsIncludedActive,
      isFilterCountLoading,
      inventoryCount,
      hasAnyOptions: hasAnyBillsIncludedOptions(dynamiceFilterCount?.data),
      hasFilterCountPayload: !!dynamiceFilterCount?.data,
    })
  ) {
    ORDERED_FILTERS = ORDERED_FILTERS?.filter((filter: any) => filter?.name !== "Bills Included");
  }

  const isAmenitiesActive = !!getFilterActiveStatus("Amenities", filters);
  if (
    shouldHideAmenitiesOrBillsPill({
      isActive: isAmenitiesActive,
      isFilterCountLoading,
      inventoryCount,
      hasAnyOptions: hasAnyAmenitiesOptions(dynamiceFilterCount?.data),
      hasFilterCountPayload: !!dynamiceFilterCount?.data,
    })
  ) {
    ORDERED_FILTERS = ORDERED_FILTERS?.filter((filter: any) => filter?.name !== "Amenities");
  }

  const growthBook = useGrowthBook();
  const isGrowthBookReady = growthBook?.ready ?? false;
  const smartFiltersFlag = useFeatureIsOn(GROWTH_BOOK_FEATURE_FLAG_NAMES.SMART_FILTERS);
  const hideLocalityFromRail = !config.IS_CHINA_APP && (!isGrowthBookReady || smartFiltersFlag);
  const isLocalityActive = !!getFilterActiveStatus("Locality", filters);
  if (hideLocalityFromRail) {
    ORDERED_FILTERS = ORDERED_FILTERS.filter((filter: any) => filter?.name !== "Locality");
    if (isLocalityActive && LOCALITY_FILTER) {
      ORDERED_FILTERS = [LOCALITY_FILTER, ...ORDERED_FILTERS];
    }
  }

  const activeFilters = ORDERED_FILTERS.filter(
    ({ title }: { title: string }) => !!getFilterActiveStatus(title as FilterNameType, filters),
  );
  const nonActiveFilters = ORDERED_FILTERS.filter(
    ({ title }: { title: string }) => !getFilterActiveStatus(title as FilterNameType, filters),
  );

  const showShimmerForFiltersFlag = useFeatureIsOn(
    GROWTH_BOOK_FEATURE_FLAG_NAMES.SHOW_FILTERS_SHIMMER_ANIMATION,
  );
  const shouldAnimate = useAnimationWithCookie(4000, "filter_badge_animation_shown", 30);
  const badgeRef = useRef<HTMLDivElement>(null);
  const isSmartFiltersEnabled = !config.IS_CHINA_APP && smartFiltersFlag;
  const isNewNativeApp = useIsNewNativeApp();

  const localityOrUniversityTitle = getLocalityOrUniversityTitle(
    countryShortName,
    sourceCountry,
    filtersState,
  );

  const shouldShowAnimation = (title: string, isActive: boolean) =>
    localityOrUniversityTitle === title && shouldAnimate && showShimmerForFiltersFlag && !isActive;

  const renderBadge = (props: BadgePropsType & { translationKey?: string }) => {
    const { name, title, whitelabelTitle, activeLabel, isActive, icon, index, translationKey } =
      props;
    const isHousingType = title === "Housing Type";
    const iconComponent = isHousingType ? HousingTypeChevron : icon;
    const badgeValue =
      translationKey || getBadgeValue(isWhitelabel, isUniversityPartner, whitelabelTitle, title);
    const showAnimation = shouldShowAnimation(title, isActive);
    const badgeProps = {
      ref: badgeRef,
      value: badgeValue,
      activeLabel,
      isActive,
      onClick: () => onFilterClick(name),
      icon: iconComponent,
      className: formatClassNames(
        styles.badge,
        isHousingType && styles.housingTypeBadge,
        showAnimation && styles.animateBadge,
        isNewNativeApp && badgeStyles.nativeAppBadge,
        isNewNativeApp && isActive && badgeStyles.nativeAppBadgeActive,
      ),
      showIconOnRight: isHousingType,
    };
    const key = `${name}_${index}`;

    return <Badge key={key} {...badgeProps} />;
  };
  const filtersToRender =
    shouldShowHousingTypeFilter && housingTypeFilter
      ? [housingTypeFilter, SORT_FILTER, ...activeFilters, ...nonActiveFilters]
      : [SORT_FILTER, ...activeFilters, ...nonActiveFilters];

  // Add Housing Fest pill at the beginning (after Sort) when sale is available
  const finalFiltersToRender =
    isSaleOfferAvailable && HOUSING_FEST_FILTER
      ? (() => {
          const sortIndex = filtersToRender.findIndex((f: any) => f.name === "Sort");
          const result = [...filtersToRender];
          result.splice(sortIndex + 1, 0, HOUSING_FEST_FILTER);
          return result;
        })()
      : filtersToRender;

  return (
    <>
      {/* @ts-ignore */}
      <>
        {region?.location?.country?.long_name ? (
          <>
            {!config.IS_CHINA_APP &&
              (!isGrowthBookReady ? (
                <div className={styles.smartFilterSkeleton} />
              ) : (
                isSmartFiltersEnabled && <SmartFilterPill />
              ))}
            {finalFiltersToRender.map(
              ({ title, name, icon, whitelabelTitle, translationKey }: any, index: any) => {
                const isActive = getFilterActiveStatus(title as FilterNameType, filters);
                const activeLabel =
                  isActive &&
                  getFilterLabel(
                    title as FilterNameType,
                    filters,
                    region,
                    currencyExchangeRates,
                    t,
                    budgetPriceCeiling,
                    filterUniversityList,
                    filterLocalityList,
                  );
                const showSeparator = shouldShowHousingTypeFilter && index === 0;
                return (
                  <>
                    {renderBadge({
                      name,
                      title,
                      whitelabelTitle,
                      activeLabel,
                      isActive,
                      icon,
                      index,
                      translationKey:
                        title === "Housing Fest"
                          ? translationKey || "searchpage.searchlist.filtersubsection.housingfest"
                          : undefined,
                    })}
                    {showSeparator && (
                      <div
                        className={styles.separator}
                        data-testid="housing-type-separator-mobile"
                      />
                    )}
                  </>
                );
              },
            )}
          </>
        ) : (
          <>
            {Array.from({ length: 5 }, (_, index) => index + 1).map((ele) => (
              <FiltersBadgeLazyLoader key={ele} />
            ))}
          </>
        )}
      </>
    </>
  );
};

export default wrapperHOC(FiltersBadgeGroup, {
  componentName: "FiltersBadgeGroup-FilterSection-SearchPage",
  showForChina: true,
});
